
import Vue from 'vue';
import _keyBy from 'lodash/keyBy';
import moment from 'moment-timezone';
import { DbTimestamp, Team, TeamMember } from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Timestamp } from '@/firebase';
import { CursorListMixin, FiltersPanelMixin, JustAddedRowsMixin, TeamUserMixin } from '@/mixins';
import { JUST_ADDED_LIST } from '@/utils/justAdded';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { StoredLastPagination } from '@/store';
import { ListFetchResult, restartWalk, walkPageNumber } from '@/utils/cursorWalk';
import { HydratedListView, hydrateListView, rememberListView } from '@/utils/listView';
import { normalizePanelFilters } from '@/utils/panelFilters';
import { TEAMS_API_BASE_URL, TeamMemberListRow, fetchTeamMembersPage } from '@/utils/teamsApi';
import { MirrorLagChannel } from '@/utils/mirrorLagChannel';
import { TEAMS_ADDED, noteTeamsAdded, noteTeamsChanged } from '@/utils/teamsMirrorLag';
import { fetchTeamMembersByKey, withFreshTeamMembers } from '@/services/teamDocs';
import {
	MEMBERSHIP_STATUS_ITEMS,
	TEAM_MEMBERS_DEFAULT_OPTIONS,
	TEAM_MEMBERS_HEADER_VALUE,
	TEAM_MEMBERS_RESTING_FILTERS,
	TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
	TableHeaderValue,
	TeamMemberDownloadRow,
	TeamMembersPanelFilters,
	buildTeamMembersRequest,
	isSortableTeamMembersHeader,
	stubTeamMemberFromRow,
	teamMemberDownloadHeaders,
	teamMemberDownloadRow,
	teamTablesDownloadSort,
} from '@/views/Team/teamTablesState';
import InviteTeamMember from '@/views/Team/TeamDashboard/InviteTeamMember.vue';
import TeamMemberRow from '@/views/Team/TeamDashboard/TeamMemberRow.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LiveDoc from '@/components/LiveDoc.vue';
import TableToolbar from '@/components/Filters/TableToolbar.vue';
import FiltersPanel from '@/components/Filters/FiltersPanel.vue';

/**
 * One Team's members, the way the Directory lists the company's: Postgres (through
 * api2) picks and orders each page, filtered by the search and the Filters box,
 * and every row binds its own membership document and stays live (LiveDoc).
 * Paging and the mirror-lag refetch are CursorListMixin's (it also hears the
 * teams mirror-lag channel for additions, so someone invited anywhere catches up
 * here; an edit stays where it is until Refresh); the box is FiltersPanelMixin's.
 * Row and bulk actions hand TeamPage's dialogs the memberships read fresh. The
 * view (sort, page size, search, filters, page) is remembered per Team.
 */
export default Vue.extend({
	name: 'TeamMembersManagement',
	mixins: [
		TeamUserMixin,
		CursorListMixin,
		FiltersPanelMixin,
		JustAddedRowsMixin,
	],
	props: {
		currTeam: { type: Object, default: null },
		currTeamUserRole: { type: Object, default: null },
		teamUserRoleDefs: { type: Array, default: () => [] },
	},
	data () {
		return {
			options: { ...TEAM_MEMBERS_DEFAULT_OPTIONS },
			itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
			searchQuery: '',
			// What the listing was fetched with. The Filters box edits a draft of it.
			appliedFilters: { ...TEAM_MEMBERS_RESTING_FILTERS } as TeamMembersPanelFilters,
			hydratedTeamId: null as string | null,
			// Kept across pages; a new search, filter or Team clears it.
			selectedRows: [] as TeamMemberListRow[],
			loadingBulkMembers: false,
			statusFilterItems: MEMBERSHIP_STATUS_ITEMS,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'companyDashboard',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		teamId (): string | null {
			return this.currTeam?.id || null;
		},
		canManageMembers (): boolean {
			return !!(this.hasManageTeamsPermission || this.checkManageTeamMembersPermission(this.currTeam, this.currTeamUserRole));
		},
		canBulkManageMembers (): boolean {
			return !this.scimProvisioningLocksMembership && this.canManageMembers;
		},
		showTeamRoleColumn (): boolean {
			return !!(this.companyClubOfferBenefits.isEnterprise && this.canManageMembers && this.currTeam?.isActive);
		},
		// FiltersPanelMixin: the box at rest, and what is applied
		restingPanelFilters (): TeamMembersPanelFilters {
			return TEAM_MEMBERS_RESTING_FILTERS;
		},
		appliedPanelFilters (): TeamMembersPanelFilters {
			return this.appliedFilters;
		},
		// CursorListMixin: whether there is a Team to read, and how to say a read failed
		listingReady (): boolean {
			return !!(this.teamId && this.currTeam?.companyId);
		},
		listingPending (): boolean {
			return !this.currTeam;
		},
		// CursorListMixin: a Team or membership added anywhere in the app
		listChangeChannel (): MirrorLagChannel {
			return TEAMS_ADDED;
		},
		// JustAddedRowsMixin: an address invited here shows on top before the listing has it
		justAddedListKey (): string | null {
			return this.teamId ? JUST_ADDED_LIST.teamMembers(this.teamId) : null;
		},
		listFetchFailureMessages (): Record<string, string> {
			return {
				unauthorized: 'Your session has expired, sign in again to view members',
				forbidden: `You do not have access to this ${this.displayLabels.team}'s members`,
				network: 'Could not reach the server, check your connection and try again',
				failed: 'Could not load members, please try again',
			};
		},
		headers () {
			return [
				{
					text: 'Email',
					value: TEAM_MEMBERS_HEADER_VALUE.EMAIL,
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				...(this.showTeamRoleColumn ? [{
					noDownload: true,
					text: `${this.displayLabels.team} Role`,
					value: TEAM_MEMBERS_HEADER_VALUE.TEAM_ROLE,
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
				}] : []),
				{
					noDownload: true,
					text: 'Joined',
					value: TEAM_MEMBERS_HEADER_VALUE.JOINED,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				...(this.canBulkManageMembers ? [
					{
						noDownload: true,
						text: 'Actions',
						value: TEAM_MEMBERS_HEADER_VALUE.ACTIONS,
						align: 'right',
						class: 'py-0 pr-4',
						sortable: false,
					},
				] : []),
			];
		},
		// The CSV is built from the Postgres rows alone: the selection is a list of them.
		downloadRows (): TeamMemberDownloadRow[] {
			return this.selectedRows.map((row: TeamMemberListRow) => teamMemberDownloadRow(row));
		},
		downloadHeaders (): TableHeaderValue[] {
			return teamMemberDownloadHeaders(this.showTeamRoleColumn, this.displayLabels.team);
		},
		downloadSort (): { sortBy: string[]; sortDesc: boolean[] } {
			return teamTablesDownloadSort(this.options, [TEAM_MEMBERS_HEADER_VALUE.EMAIL, TEAM_MEMBERS_HEADER_VALUE.JOINED]);
		},
		isNarrowed (): boolean {
			return !!this.searchQuery || this.appliedFilterCount > 0;
		},
		emptyStateMessage (): string {
			if (this.failed) return 'Could not load members';
			if (this.isNarrowed) return 'No members match your search or filters';
			return `No ${this.displayLabels.team} members yet`;
		},
	},
	watch: {
		// Another Team is another listing: its own remembered view, nothing selected, from the top.
		teamId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal === oldVal) return;
				this.clearSelectedRows();
				this.walk = restartWalk();
				this.hydrateView(newVal);
				this.scheduleFetch();
			},
		},
	},
	beforeDestroy () {
		if (this.teamId) {
			this.storeLastPagination({
				key: 'lastTeamPagination',
				pagination: rememberListView(this.options, this.searchQuery, this.appliedFilters, this.walk.cursor, walkPageNumber(this.walk)),
				teamId: this.teamId,
			});
		}
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'storeLastPagination',
		]),
		hydrateView (teamId: string | null) {
			if (!teamId || this.hydratedTeamId === teamId) return;
			this.hydratedTeamId = teamId;
			const stored: StoredLastPagination | undefined = this.companyDashboard?.lastTeamPagination?.[teamId];
			const view: HydratedListView<TeamMembersPanelFilters> = hydrateListView(
				stored,
				TEAM_MEMBERS_DEFAULT_OPTIONS,
				TEAM_MEMBERS_RESTING_FILTERS,
				{ isSortable: isSortableTeamMembersHeader, itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS },
			);
			this.options = view.options;
			this.searchQuery = view.searchQuery || '';
			this.appliedFilters = view.filters || { ...TEAM_MEMBERS_RESTING_FILTERS };
			this.walk = view.walk;
			this.syncFiltersPanel();
		},
		fetchListPage ({ idToken, cursor }: { idToken: string; cursor: string | null }): Promise<ListFetchResult<TeamMemberListRow>> {
			return fetchTeamMembersPage({
				baseUrl: TEAMS_API_BASE_URL,
				idToken,
				request: buildTeamMembersRequest({
					teamId: this.teamId,
					companyId: this.currTeam?.companyId || null,
					accountTeamId: this.team?.id || null,
					cursor,
					options: this.options,
					searchQuery: this.searchQuery,
					filters: this.appliedFilters,
				}),
			});
		},
		// A selected member back on screen is selected as this copy, so the CSV reads the newer one.
		onListRowsShown (rows: TeamMemberListRow[]) {
			const shownByKey: Record<string, TeamMemberListRow> = _keyBy(rows, 'key');
			if (this.selectedRows.some((row: TeamMemberListRow) => shownByKey[row.key] && shownByKey[row.key] !== row)) {
				this.selectedRows = this.selectedRows.map((row: TeamMemberListRow) => shownByKey[row.key] || row);
			}
		},
		// FiltersPanelMixin: Apply and Clear all land here
		commitPanelFilters (next: TeamMembersPanelFilters) {
			this.clearSelectedRows();
			this.appliedFilters = normalizePanelFilters(TEAM_MEMBERS_RESTING_FILTERS, next);
			this.resetToFirstPage();
		},
		setSearchQuery (query: string) {
			const next: string = query || '';
			if (next === this.searchQuery) return;
			this.clearSelectedRows();
			this.searchQuery = next;
			this.resetToFirstPage();
		},
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		memberDocPath (row: TeamMemberListRow): string {
			return `teams/${this.teamId}/teamMembers/${row.key}`;
		},
		stubFor (row: TeamMemberListRow): TeamMember {
			return stubTeamMemberFromRow(row, (iso: string): DbTimestamp => Timestamp.fromMillis(moment(iso).valueOf()) as DbTimestamp);
		},
		// An invite went out: every Team table and count catches up.
		onMembersInvited () {
			this.clearSelectedRows();
			noteTeamsAdded();
		},
		// A dialog changed someone: the counts catch up, and the rows follow it where they are.
		onMembersChanged () {
			this.clearSelectedRows();
			noteTeamsChanged();
		},
		// A row's membership is gone: never act on it, and ask again later.
		onRowRemoved (key: string) {
			this.selectedRows = this.selectedRows.filter((row: TeamMemberListRow) => row.key !== key);
			this.forgetJustAddedRow(key);
			this.refetchAfterMirrorLag();
		},
		// JustAddedRowsMixin: a membership's id is its address, the row's key
		justAddedRowId (row: TeamMemberListRow): string {
			return row.key;
		},
		/**
		 * An action, passed up to TeamPage (which owns the dialogs) with an onSuccess
		 * that clears the selection and lets the counts catch up.
		 */
		reemitWithRefetch (eventName: string, payload: Record<string, unknown>) {
			this.$emit(eventName, {
				...payload,
				onSuccess: () => this.onMembersChanged(),
			});
		},
		/**
		 * A row's own action. The row may still be showing its Postgres stand-in, so the
		 * dialog gets the memberships read fresh, as the bulk actions do.
		 */
		async onRowAction (eventName: string, payload: Record<string, unknown>) {
			if (!this.teamId) return;
			let fresh: Record<string, unknown> | null;
			try {
				fresh = await withFreshTeamMembers(this.teamId, payload);
			} catch (error) {
				console.error('Error loading the member:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Could not load this member, please try again',
					show: true,
				});
				return;
			}
			if (this.isDestroyed) return;
			if (!fresh) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `This member is no longer on this ${this.displayLabels.team}`,
					show: true,
				});
				return;
			}
			this.reemitWithRefetch(eventName, fresh);
		},
		/**
		 * The bulk Roles and Remove edit the memberships themselves, so the dialogs
		 * get them as they are now, read by key, rather than the mirror's rows,
		 * which can be a few seconds behind, and which may span pages.
		 */
		async onBulkTeamMembers (eventName: 'edit-team-members' | 'remove-team-members') {
			if (!this.selectedRows.length || this.loadingBulkMembers || !this.teamId) return;
			this.loadingBulkMembers = true;
			try {
				const teamMembers: TeamMember[] = await fetchTeamMembersByKey(this.teamId, this.selectedRows.map((row: TeamMemberListRow) => row.key));
				if (this.isDestroyed) return;
				if (!teamMembers.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'The selected members are no longer on this ' + this.displayLabels.team,
						show: true,
					});
					return;
				}
				this.reemitWithRefetch(eventName, { teamMembers, team: this.currTeam as Team });
			} catch (error) {
				console.error('Error loading the selected members:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Could not load the selected members, please try again',
					show: true,
				});
			} finally {
				this.loadingBulkMembers = false;
			}
		},
	},
	components: {
		BaseButton,
		EmptyState,
		FiltersPanel,
		InviteTeamMember,
		LiveDoc,
		TableBulkActionsPopover,
		TableToolbar,
		TeamMemberRow,
	},
});
